import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FirebaseRemoteConfigService } from '@core/services/common';
import { PatrimonialContextService } from '@core/services/context/patrimonial-context.service';
import { AuthMirrorService } from '@core/services/core/auth/auth-mirror.service';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { VehicleAccidentsHelperService } from '@core/services/core/vehicle-accidents-helper.service';
import { Platform } from '@ionic/angular';
import { AllFeatureFlags } from '@models/mmobile';
import { OpinatorConfig, PollConfig, PollData } from '@models/poll-data';
import { OpinatorComponent } from '@modules/client-area/components/opinator/opinator.component';
import { getRandomNumber, isProduction } from '@utils/utils';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OpinatorService {
  private readonly VERIFY_URL = 'https://www.opinator.com/api/visible.json';
  private readonly BASE_URL = 'https://www.opinator.com/opi/';
  pollData: PollData;
  opinatorConfig: OpinatorConfig;

  constructor(
    private ctx: ContextService,
    private authMirror: AuthMirrorService,
    private vehicleAccidentsHelper: VehicleAccidentsHelperService,
    private patrimonialContext: PatrimonialContextService,
    private mmobileHelper: MMobileHelperService,
    private firebaseConfig: FirebaseRemoteConfigService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private ui: UiHelperService,
    private platform: Platform
  ) { }

  async init() {
    if (this.isOpinatorSdkFeatureEnabled()) {
      this.opinatorConfig = JSON.parse((await this.firebaseConfig.getConfigValue('opinatorConfig')).asString());
      this.setupVehicleAccidentsResult();
      this.ctx.onPageChange$().subscribe(async url => {
        this.checkShowOpinatorForm();
      });
    }
  }

  private isOpinatorSdkFeatureEnabled() {
    return this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.SDK_OPINATOR);
  }

  private setupVehicleAccidentsResult() {
    this.vehicleAccidentsHelper.getUpdates('vehicle-accidents-result-ok').subscribe({
      next: _ => {
        this.pollData.result = 'ok';
        this.pollData.area_type = 'Logado';
      }
    });
    this.vehicleAccidentsHelper.getUpdates('vehicle-accidents-result-ko').subscribe({
      next: _ => {
        this.pollData.result = 'ko';
        this.pollData.area_type = 'Logado';
      }
    });
  }

  private checkShowOpinatorForm() {
    this.saveOpinatorCustomParameters();
    Object.entries(this.opinatorConfig?.polls).forEach(([pollKey, pollConfigValue]) => {
      if (this.checkMatches(pollConfigValue) && this.passesRandomnessCheck(pollConfigValue)) {
        this.initProcess(pollConfigValue);
      }
    });
  }

  private initProcess(poll: PollConfig) {
    const opiId = `${poll?.id}${isProduction() ? '' : '_PRE'}`;
    if (poll?.saturationControl) {
      const verifyUrl = `${this.VERIFY_URL}?id=${this.pollData?.cod_ric}&designator=${opiId}`;
      this.getOpiEnabledByVerificationUrl(verifyUrl).subscribe(canShowOpi => {
        if (canShowOpi) {
          this.openOpi(opiId);
        }
      });
    } else {
      this.openOpi(opiId);
    }
  }

  private getOpiEnabledByVerificationUrl(verifyUrl: string) {
    return this.http.get(verifyUrl).pipe(
      switchMap((res: any) => of(res && res.responseObject === true))
    );
  }

  private openOpi(opiId: string) {
    const params = new HttpParams().append('id', this.pollData?.cod_ric);
    const url = `${this.BASE_URL}${opiId}?${this.getOpinatorExtraParams(params)}`;
    return this.ui.openComponentAsModal(
      OpinatorComponent,
      { iframeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url) },
      'opinator-modal'
    );
  }

  private saveOpinatorCustomParameters() {
    const source = this.getOpinatorSource();
    const user = this.authMirror.authService.getState().isLogged
      ? this.authMirror.authService.getState().user
      : this.patrimonialContext.getPatrimonialsClientInfo();
    this.pollData = {
      ...this.pollData,
      user_type: this.authMirror.authService.checkUserIsCompany() ? 'company' : 'particular',
      cod_ric: user?.userId,
      source
    };
  }

  private getOpinatorSource() {
    return this.platform.is('cordova')
     ? this.platform.is('ios') ? 'App_iOS' : 'App_Android'
     : 'Web';
  }

  private getOpinatorExtraParams(params = new HttpParams()) {
    Object.entries(this.pollData).forEach(([name, value]) => {
      params = params.append(`carry_${name}`, value);
    });
    return params.toString();
  }

  // If poll has a reach number value, generate a random number and return true if random is less than the poll reach
  private passesRandomnessCheck(pollConfigValue: PollConfig) {
    if (typeof pollConfigValue.reach !== 'number') { return true; }
    return getRandomNumber() < pollConfigValue.reach;
  }

  private checkMatches(poll: PollConfig){
    return poll?.enabled
      && this.ctx.previousPage.includes(poll?.match?.previousPage)
      && poll?.userType.includes(this.pollData?.user_type)
      && (this.ctx.currentPage === poll?.match?.nextPage || this.pollData?.result === poll?.match?.result);
  }
}
